import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Package, RefreshCw, Search } from 'lucide-react';
import { productService } from '../../services/api';
import ProductGrid from './components/ProductGrid';
import Pagination from './components/Pagination';
import { cn } from '../../lib/utils';
import NormalizedProductModal from '../../components/common/NormalizedProductModal';
import { useUI } from '../../context/UI';
import VendorProductFilterForm from './VendorProductFilterForm';
import CustomModal from '../../components/ui/CustomModal';
import Button from '../../components/ui/button/Button';
import { FilterIcon } from '../../icons';
import PageMeta from '../../components/common/PageMeta';
import { DEFAULT_PAGE_DESCRIPTION, DEFAULT_PAGE_TITLE } from '../../utilities/constants';
import PageBreadcrumb from '../../components/common/PageBreadCrumb';

const ProductsByVendor = () => {
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState('');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingVendors, setLoadingVendors] = useState(true);
  const { addToastMessage } = useUI();

  const [searchTerm, setSearchTerm] = useState('');

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [fetchingNormalizedId, setFetchingNormalizedId] = useState(null);
  const [advancedFilterParams, setAdvancedFilterParams] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrevious, setHasPrevious] = useState(false);

  // Fetch vendors on mount
  useEffect(() => {
    fetchVendors();
  }, []);

  // Fetch products when vendor or page changes or advanced filters applied
  useEffect(() => {
    if (selectedVendor || advancedFilterParams) {
      fetchProducts();
    }
  }, [selectedVendor, currentPage, advancedFilterParams]);

  const fetchVendors = async () => {
    setLoadingVendors(true);
    try {
      const response = await productService.getAllVendors();
      setVendors(response.data);
      if (response.data.length > 0) {
        setSelectedVendor(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching vendors:', error);
    } finally {
      setLoadingVendors(false);
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      let response;
      if (advancedFilterParams) {
        response = await productService.searchPartnerProducts(advancedFilterParams);
      } else {
        if (!selectedVendor) {
          setLoading(false);
          return;
        }
        response = await productService.getProductsByVendor(
          selectedVendor,
          currentPage,
          pageSize
        );
      }

      const data = response.data;
      if (advancedFilterParams) {
        // Map the new API response format
        const rawList = data?.data || [];
        const productList = rawList.map(item => ({
          id: item.productId,
          name: item.product?.title,
          brand: item.product?.brand,
          image: item.product?.media?.primaryImage,
          price: item.product?.pricing?.discount?.percentage
            ? Number((item.product.pricing.basePriceUsd * (1 - item.product.pricing.discount.percentage / 100)).toFixed(2))
            : item.product?.pricing?.basePriceUsd,
          originalPrice: item.product?.pricing?.basePriceUsd,
          discount: item.product?.pricing?.discount?.percentage,
          url: item.partnerInfo?.affiliateLink,
          processed: item.productFound,
          currency: 'USD'
        }));

        console.log("mapped productList", productList);
        setProducts(productList);
        setTotalPages(1);
        setTotalElements(productList.length);
        setHasNext(false);
        setHasPrevious(false);
      } else {
        console.log("data ELSEE", data);
        setProducts(data.content);
        setTotalPages(data.totalPages);
        setTotalElements(data.totalElements);
        setHasNext(data.hasNext);
        setHasPrevious(data.hasPrevious);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  console.log("ProductsByVendor", products);

  const handleVendorChange = (vendor) => {
    setSelectedVendor(vendor);
    setCurrentPage(0); // Reset to first page when vendor changes
  };

  const handleViewNormalizedProduct = async (product) => {
    if (!product || !product.id) return;

    setFetchingNormalizedId(product.id);
    try {
      const response = await productService.getNormalizedProduct(product.id);

      if (response && response.data) {
        setSelectedProduct(response.data);
        setIsModalOpen(true);
      } else {
        addToastMessage({
          status: "warning",
          message:
            "No normalized data found.",
        });
        console.warn("No normalized data found");
      }
    } catch (error) {
      addToastMessage({
        status: "warning",
        message:
          "No normalized data found.",
      });
      console.error("Error fetching normalized product:", error);
    } finally {
      setFetchingNormalizedId(null);
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const filteredProducts = products?.filter(p =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.brand.toLowerCase().includes(searchTerm.toLowerCase())
  );
  console.log("filteredProducts", filteredProducts);


  return (
    <div className="max-w mx-auto px- py-">
      <PageMeta
        title={`Products by Vendor - ${DEFAULT_PAGE_TITLE}`}
        description={`Products by Vendor - ${DEFAULT_PAGE_DESCRIPTION}`}
      />
      <PageBreadcrumb
        pageTitle="Products by Vendor"
      />
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <p className="text-gray-600 dark:text-gray-300">Browse products filtered by vendor with pagination</p>
        <div className="flex gap-2">
          {advancedFilterParams && (
            <Button
              variant="canel"
              onClick={() => setAdvancedFilterParams(null)}
            >
              Clear Filters
            </Button>
          )}
          <CustomModal
            selector={
              <Button
                variant={advancedFilterParams ? "primary" : "secondary"}
                startIcon={<FilterIcon className="w-5 h-5" />}
              >
                Filter
              </Button>
            }
            content={
              <VendorProductFilterForm
                onSubmit={setAdvancedFilterParams}
                initialValues={advancedFilterParams}
                setShow={() => { }}
              />
            }
          />
        </div>
      </div>

      {/* Vendor Selection & Search */}
      <div className="flex flex-col md:flex-row gap-4 mb-8">
        {/* Vendor Dropdown */}
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Select Vendor
          </label>
          {loadingVendors ? (
            <div className="h-12 bg-gray-100 border border-gray-200 dark:bg-gray-800 dark:border-gray-700 rounded-lg animate-pulse" />
          ) : (
            <div className="relative">
              <Package className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-300" />
              <select
                value={selectedVendor}
                onChange={(e) => handleVendorChange(e.target.value)}
                className="w-full bg-white border dark:bg-gray-800 border-gray-200 text-gray-900 dark:text-gray-300 pl-11 pr-4 py-3 rounded-lg focus:ring-2 focus:ring-brand-500/50 focus:border-brand-500 transition-all appearance-none cursor-pointer"
              >
                {vendors.map((vendor) => (
                  <option key={vendor} value={vendor}>
                    {vendor}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Search */}
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Search Products
          </label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name or brand..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-white border border-gray-200 dark:bg-gray-800 text-gray-900 dark:text-gray-300 pl-11 pr-4 py-3 rounded-lg focus:ring-2 focus:ring-brand-500/50 focus:border-brand-500 transition-all"
            />
          </div>
        </div>

        {/* Refresh Button */}
        <div className="flex items-end">
          <button
            onClick={() => setSearchTerm("") || fetchProducts()}
            disabled={loading || !selectedVendor}
            className="p-3 bg-white border border-gray-200 rounded-lg text-gray-500 hover:text-brand-600 hover:border-brand-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            title="Refresh Products"
          >
            <RefreshCw className={cn("w-5 h-5", loading && "animate-spin")} />
          </button>
        </div>
      </div>

      {/* Stats */}
      {selectedVendor && !loading && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 p-4 bg-white border border-gray-200 rounded-lg shadow-sm dark:bg-gray-700 dark:border-gray-600"
        >
          <div className="flex items-center justify-between ">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-300 ">
                Showing <span className="font-medium text-gray-900 dark:text-gray-300">{filteredProducts.length}</span> of{' '}
                <span className="font-medium text-gray-900 dark:text-gray-300">{totalElements}</span> products {advancedFilterParams ? 'found' : `from `}
                {!advancedFilterParams && <span className="font-medium text-brand-600">{selectedVendor}</span>}
              </p>
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Page {currentPage + 1} of {totalPages}
            </div>
          </div>
        </motion.div>
      )}

      {/* Product Grid */}
      <ProductGrid
        products={filteredProducts}
        loading={loading}
        fetchingNormalizedId={fetchingNormalizedId}
        onViewNormalizedClick={handleViewNormalizedProduct}
        onProductClick={(product) => {
          if (product.processed) {
            handleViewNormalizedProduct(product);
          }
        }}
      />

      {/* Normalized Product Modal */}
      <NormalizedProductModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        data={selectedProduct}
      />

      {/* Pagination */}
      {!loading && filteredProducts.length > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalElements={totalElements}
          hasNext={hasNext}
          hasPrevious={hasPrevious}
          onPageChange={handlePageChange}
        />
      )}
    </div>
  );
};

export default ProductsByVendor;
